<template>
  <div class="mb-4">
    <div class="bg-white border border-gray-200 p-3 mb-4">
      <div class="flex justify-between">
        <div>
          <div class="font-medium">Ticket flow</div>
          <div class="text-gray-500">
            Weekly tickets in and out, with open backlog on the right axis.
          </div>
        </div>
        <select v-model="flowGroup" class="border border-gray-300 p-1">
          <option value="none">No split</option>
          <option value="priority">Split by priority</option>
          <option value="customer">Split by customer</option>
        </select>
      </div>
      <p v-if="!flow.length" class="text-gray-500 py-8">
        No ticket movement in the last 12 weeks.
      </p>
      <Chart_V5
        v-else
        :chart_data="flowData"
        :chart_config="flowConfig"
        :show-export="false"
        override-height="h-[340px]"
      />
    </div>

    <div class="bg-white border border-gray-200 p-3">
      <div class="flex justify-between">
        <div>
          <div class="font-medium">Engineering work</div>
          <div class="text-gray-500">Pull requests merged per week, by category.</div>
        </div>
        <select v-model="engineer" class="border border-gray-300 p-1">
          <option :value="null">All engineers</option>
          <option v-for="name in engineers" :key="name" :value="name">{{ name }}</option>
        </select>
      </div>
      <p v-if="!workData.series.length" class="text-gray-500 py-8">
        No merged pull requests to show.
      </p>
      <Chart_V5
        v-else
        :chart_data="workData"
        :chart_config="workConfig"
        :show-export="false"
        override-height="h-[340px]"
      />
    </div>
  </div>
</template>

<script>
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import { CATEGORY_LABELS, canonicalPriority, label } from './hades_constants';

const PRIORITY_ORDER = ['p0', 'p1', 'p2', 'p3', 'none'];
const PRIORITY_LABELS = {
  p0: 'P0 Critical',
  p1: 'P1 High',
  p2: 'P2 Medium',
  p3: 'P3 Low',
  none: 'No priority',
};
const PRIORITY_COLORS = {
  p0: '#D53D3D',
  p1: '#F76734',
  p2: '#F1BD6C',
  p3: '#88A484',
  none: '#D2D2D3',
};
const GROUP_COLORS = ['#F76734', '#4C6E97', '#88A484', '#F1BD6C', '#8E8F90', '#B7ACFC'];

const CATEGORY_COLORS = {
  customer_feature: '#F76734',
  bug_fix: '#D53D3D',
  app_stability_reliability: '#4C6E97',
  architecture_platform: '#88A484',
  data_integration: '#F1BD6C',
  security_compliance: '#8D84E8',
  internal_tooling: '#A5A5A6',
  documentation: '#D2D2D3',
  maintenance: '#78797A',
  unclassified: '#E8E9E9',
};

export default {
  name: 'Hades_Charts',
  components: { Chart_V5 },
  props: {
    flow: { type: Array, required: true },
    pullRequests: { type: Array, required: true },
    customer: { type: String, default: null },
  },
  data() {
    return { engineer: null, flowGroup: 'none' };
  },
  computed: {
    engineers() {
      return [...new Set(this.pullRequests
        .map((pr) => pr.engineer_id || pr.author_login)
        .filter(Boolean))].sort();
    },
    weeks() {
      return [...new Set(this.flow.map((row) => row.week_start))].sort();
    },
    flowData() {
      const rows = this.flow.filter(
        (row) => !this.customer || row.customer === this.customer);

      // IN ABOVE THE LINE, OUT BELOW IT — one diverging stack per week. Closed
      // values are NEGATED and share New's offset, so the bar reads as a single
      // gesture: is the queue growing or shrinking.
      //
      // This needed a Chart_V5 fix. Its bar height was
      // `min(scale(start), innerHeight) - scale(end)`, which goes negative for a
      // negative value, and an SVG rect with negative height silently does not
      // render. Now min/abs, and the domain tracks the stack's negative extent.
      //
      // flowGroup only decides how each side is SPLIT — nothing, priority, or
      // customer. It is not a choice about in vs out.
      const perWeek = {};
      rows.forEach((row) => {
        const week = perWeek[row.week_start] || { incoming: 0, outgoing: 0 };
        week.incoming += row.incoming;
        week.outgoing += row.outgoing;
        perWeek[row.week_start] = week;
      });

      let running = 0;
      const backlogSeries = {
        values: this.weeks.map((week) => {
          const w = perWeek[week] || { incoming: 0, outgoing: 0 };
          running += w.incoming - w.outgoing;
          return running;
        }),
        config: {
          label: 'Open backlog (cumulative)',
          backgroundColor: '#353537',
          type: 'line',
          yAxis: 'right',
          offset: 0,
        },
      };

      if (this.flowGroup === 'none') {
        return {
          labels: this.weeks.map(this.weekLabel),
          series: [
            {
              values: this.weeks.map((w) => perWeek[w]?.incoming || 0),
              config: { label: 'Opened', backgroundColor: '#F76734',
                        type: 'bar', yAxis: 'left', offset: 0 },
            },
            {
              // NEGATED: closed tickets grow DOWNWARD from zero. Same offset as
              // Opened, so one bar per week reads in above the line, out below.
              // Two colours here because there is no group to colour by.
              values: this.weeks.map((w) => -(perWeek[w]?.outgoing || 0)),
              config: { label: 'Closed', backgroundColor: '#4C6E97',
                        type: 'bar', yAxis: 'left', offset: 0 },
            },
            backlogSeries,
          ],
        };
      }

      // Grouped: BOTH bars stack by the group. Same offsets, so New and Closed
      // stay side by side and each becomes a stack of its own.
      const byPriority = this.flowGroup === 'priority';
      const keyOf = (row) => (byPriority
        ? canonicalPriority(row.priority) : row.customer);
      const at = {};
      rows.forEach((row) => {
        const key = `${row.week_start}|${keyOf(row)}`;
        const cell = at[key] || { incoming: 0, outgoing: 0 };
        cell.incoming += row.incoming;
        cell.outgoing += row.outgoing;
        at[key] = cell;
      });

      const present = new Set(rows.map(keyOf));
      const groups = byPriority
        ? PRIORITY_ORDER.filter((k) => present.has(k))
        : [...present].sort();
      const groupLabel = (key) => (byPriority
        ? (PRIORITY_LABELS[key] || key) : key);
      const groupColor = (key, i) => (byPriority
        ? (PRIORITY_COLORS[key] || '#D2D2D3')
        : GROUP_COLORS[i % GROUP_COLORS.length]);

      // ONE LEGEND ENTRY PER GROUP, not two. The bar's position already says
      // whether it is new (up) or closed (down) — repeating that in the label
      // gives ten entries where five carry the information. Each group emits an
      // up series and a down series sharing a label and a colour.
      const series = [];
      groups.forEach((group, i) => {
        const name = groupLabel(group);
        const colour = groupColor(group, i);
        series.push({
          values: this.weeks.map((w) => at[`${w}|${group}`]?.incoming || 0),
          config: { label: name, backgroundColor: colour,
                    type: 'bar', yAxis: 'left', offset: 0 },
        });
        series.push({
          // Chart_V5 keys data rows by label, so the closed half needs its own
          // label — but hideFromLegend keeps it out of the legend, because one
          // group is one entry.
          values: this.weeks.map((w) => -(at[`${w}|${group}`]?.outgoing || 0)),
          config: { label: `${name} closed`, backgroundColor: colour,
                    type: 'bar', yAxis: 'left', offset: 0,
                    hideFromLegend: true },
        });
      });
      series.push(backlogSeries);

      return { labels: this.weeks.map(this.weekLabel), series };
    },
    flowConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 40, right: 55, bottom: 50, left: 50 },
        xAxis: { labelRotation: 0, fontSize: 10 },
        yAxes: [
          // ABS ticks, or the outflow half reads as negative work.
          { position: 'left', fontSize: 10, tickFormat: (v) => Math.abs(v) },
          { position: 'right', fontSize: 10 },
        ],
        legend: { display: true, position: 'right', spacing: 10 },
        // ALWAYS OFF. On a stacked bar the segment label and the bar height are
        // different numbers, so the labels overlap each other and contradict the
        // axis. The axis carries the total; the tooltip carries the split.
        labelDisplayConfig: { hideDataLabels: true },
        formatting: { percentage: false, sig_figs: 0 },
      };
    },
    workData() {
      // MERGED PER WEEK, not "open on a given day".
      //
      // The previous version counted a PR as active on every day between its
      // open and merge dates. That cannot work on this data: HadesPR.created_at
      // is the classification timestamp, and 335 of 357 rows have it AFTER
      // merged_at — so the window `opened <= day <= closed` was inverted and
      // matched zero days, leaving the chart blank.
      //
      // merged_at is a real GitHub date on 335 rows, so bucket by that. It also
      // answers a cleaner question: what shipped, and when.
      const mine = this.pullRequests.filter((pr) => pr.merged_at
        && (!this.engineer
            || (pr.engineer_id || pr.author_login) === this.engineer));
      if (!mine.length) return { labels: [], series: [] };

      const buckets = {};
      mine.forEach((pr) => {
        const week = this.weekStart(pr.merged_at);
        if (!week) return;
        buckets[week] = buckets[week] || {};
        const category = pr.primary_category || 'unclassified';
        buckets[week][category] = (buckets[week][category] || 0) + 1;
      });

      const weeks = Object.keys(buckets).sort();
      const categories = [...new Set(mine.map(
        (pr) => pr.primary_category || 'unclassified'))].sort();

      return {
        labels: weeks.map(this.weekLabel),
        // Same offset on every series = one stacked bar per week.
        series: categories.map((category) => ({
          values: weeks.map((week) => buckets[week][category] || 0),
          config: {
            label: label(CATEGORY_LABELS, category),
            backgroundColor: CATEGORY_COLORS[category] || '#D2D2D3',
            type: 'bar',
            yAxis: 'left',
            offset: 0,
          },
        })),
      };
    },
    workConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 40, right: 20, bottom: 50, left: 50 },
        xAxis: { labelRotation: 0, fontSize: 10 },
        yAxes: [{ position: 'left', fontSize: 10 }],
        legend: { display: true, position: 'top', spacing: 10 },
        // Stacked: the segment label and the bar height are different numbers,
        // so the axis carries the total and the tooltip carries the split.
        labelDisplayConfig: { hideDataLabels: true },
        formatting: { percentage: false, sig_figs: 0 },
      };
    },
  },
  methods: {
    weekStart(iso) {
      // Returns null on anything that is not a real date. A truthiness check on
      // merged_at is not enough — any non-empty string passes it, and
      // toISOString then throws RangeError and takes the whole render with it.
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return null;
      const day = date.getUTCDay();
      date.setUTCDate(date.getUTCDate() - ((day + 6) % 7));
      return date.toISOString().slice(0, 10);
    },
    weekLabel(iso) {
      return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined,
        { month: 'short', day: 'numeric' });
    },
  },
};
</script>
